import { tableFromIPC } from "apache-arrow";

export const ARROW_IPC_MEDIA_TYPE = "application/vnd.apache.arrow.stream";

// Minimal WKB → GeoJSON parser. Supports the geometry types intermix
// endpoints emit today: Point (centroided nodes / composite), LineString
// (tree edges), and the Multi* / Polygon variants we might see from
// embers. Anything else throws so callers can surface the bug instead of
// silently dropping features.
function wkbToGeoJSON(bytes) {
  if (!bytes || bytes.byteLength < 5) return null;
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const cursor = { p: 0 };
  return readGeometry(dv, cursor);
}

function readGeometry(dv, cursor) {
  const littleEndian = dv.getUint8(cursor.p) === 1;
  cursor.p += 1;
  const rawType = dv.getUint32(cursor.p, littleEndian);
  cursor.p += 4;

  // PostGIS-style extended WKB encodes Z/M/SRID in the upper bits; ISO-WKB
  // encodes them as +1000/+2000/+3000 offsets. Strip both representations.
  const isoBase = rawType % 1000;
  const baseType = rawType & 0xff || isoBase;
  const hasZExt = (rawType & 0x80000000) !== 0;
  const hasMExt = (rawType & 0x40000000) !== 0;
  const hasSRID = (rawType & 0x20000000) !== 0;
  if (hasSRID) {
    // Skip 4-byte SRID; we don't need it client-side.
    cursor.p += 4;
  }
  const isoHasZ = rawType >= 1000 && rawType < 2000;
  const isoHasM = (rawType >= 2000 && rawType < 3000) || rawType >= 3000;
  const skipPerCoord =
    (hasZExt || isoHasZ ? 8 : 0) + (hasMExt || isoHasM ? 8 : 0);

  switch (baseType) {
    case 1:
      return readPoint(dv, cursor, littleEndian, skipPerCoord);
    case 2:
      return readLineString(dv, cursor, littleEndian, skipPerCoord);
    case 3:
      return readPolygon(dv, cursor, littleEndian, skipPerCoord);
    case 4: {
      const points = readMulti(dv, cursor, readGeometry);
      return {
        type: "MultiPoint",
        coordinates: points.map((p) => p.coordinates),
      };
    }
    case 5: {
      const lines = readMulti(dv, cursor, readGeometry);
      return {
        type: "MultiLineString",
        coordinates: lines.map((l) => l.coordinates),
      };
    }
    case 6: {
      const polys = readMulti(dv, cursor, readGeometry);
      return {
        type: "MultiPolygon",
        coordinates: polys.map((p) => p.coordinates),
      };
    }
    case 7: {
      const geoms = readMulti(dv, cursor, readGeometry);
      return { type: "GeometryCollection", geometries: geoms };
    }
    default:
      throw new Error(`Unsupported WKB geometry type ${baseType}`);
  }
}

function readPoint(dv, cursor, le, skipPerCoord) {
  const x = dv.getFloat64(cursor.p, le);
  cursor.p += 8;
  const y = dv.getFloat64(cursor.p, le);
  cursor.p += 8;
  cursor.p += skipPerCoord;
  return { type: "Point", coordinates: [x, y] };
}

function readLineString(dv, cursor, le, skipPerCoord) {
  const n = dv.getUint32(cursor.p, le);
  cursor.p += 4;
  const coords = new Array(n);
  for (let i = 0; i < n; i++) {
    const x = dv.getFloat64(cursor.p, le);
    cursor.p += 8;
    const y = dv.getFloat64(cursor.p, le);
    cursor.p += 8;
    cursor.p += skipPerCoord;
    coords[i] = [x, y];
  }
  return { type: "LineString", coordinates: coords };
}

function readPolygon(dv, cursor, le, skipPerCoord) {
  const nRings = dv.getUint32(cursor.p, le);
  cursor.p += 4;
  const rings = new Array(nRings);
  for (let r = 0; r < nRings; r++) {
    const n = dv.getUint32(cursor.p, le);
    cursor.p += 4;
    const ring = new Array(n);
    for (let i = 0; i < n; i++) {
      const x = dv.getFloat64(cursor.p, le);
      cursor.p += 8;
      const y = dv.getFloat64(cursor.p, le);
      cursor.p += 8;
      cursor.p += skipPerCoord;
      ring[i] = [x, y];
    }
    rings[r] = ring;
  }
  return { type: "Polygon", coordinates: rings };
}

function readMulti(dv, cursor, readOne) {
  const n = dv.getUint32(cursor.p, dv.getUint8(cursor.p - 5) === 1);
  cursor.p += 4;
  const out = new Array(n);
  for (let i = 0; i < n; i++) {
    out[i] = readOne(dv, cursor);
  }
  return out;
}

function unwrapValue(v) {
  // Arrow JS returns BigInt for int64 columns; Mapbox paint expressions
  // and JSON.stringify both choke on those.
  if (typeof v === "bigint") return Number(v);
  return v;
}

function arrowTableToFeatureCollection(table) {
  const numRows = table.numRows;
  const fields = table.schema.fields;
  const propFields = fields
    .filter((f) => f.name !== "geometry")
    .map((f) => ({ name: f.name, column: table.getChild(f.name) }));
  const geomColumn = table.getChild("geometry");

  const features = new Array(numRows);
  for (let i = 0; i < numRows; i++) {
    const properties = {};
    for (let j = 0; j < propFields.length; j++) {
      const { name, column } = propFields[j];
      properties[name] = unwrapValue(column.get(i));
    }
    let geometry = null;
    if (geomColumn) {
      const wkb = geomColumn.get(i);
      if (wkb && wkb.byteLength > 0) {
        try {
          geometry = wkbToGeoJSON(wkb);
        } catch (err) {
          // Surface the first parse failure to the console so we notice
          // unexpected geometry types, but don't bring the whole frame down.
          if (!arrowTableToFeatureCollection._warnedWKB) {
            console.warn("intermixArrow: WKB parse failure", err);
            arrowTableToFeatureCollection._warnedWKB = true;
          }
          geometry = null;
        }
      }
    }
    features[i] = { type: "Feature", geometry, properties };
  }
  return { type: "FeatureCollection", features };
}

// Public: fetch an intermix endpoint asking for Arrow IPC and return a
// GeoJSON FeatureCollection. Transparently falls back to the legacy JSON
// path when the server doesn't ship an Arrow body.
export async function fetchIntermixGeoJSON(fetchPromise) {
  const response = await fetchPromise;
  if (!response || !response.ok) {
    return { response, data: null };
  }
  const contentType = (
    response.headers.get("content-type") || ""
  ).toLowerCase();
  if (contentType.includes("arrow")) {
    const buf = await response.arrayBuffer();
    const table = tableFromIPC(buf);
    return { response, data: arrowTableToFeatureCollection(table) };
  }
  return { response, data: await response.json() };
}

// Helper: build a fetch options object that asks the intermix API for
// Arrow IPC. Layered on top of whatever Authorization / Content-Type the
// caller already supplies.
export function withArrowAccept(options = {}) {
  return {
    ...options,
    headers: {
      ...(options.headers || {}),
      Accept: ARROW_IPC_MEDIA_TYPE,
    },
  };
}
